<template>
  <InfoPageLayout>
    <template #title>{{ t('title') }}</template>
    <p class="leading-7">{{ t('intro') }}</p>

    <section v-for="principle in principles" :key="principle.key" class="mt-12 leading-7" :aria-labelledby="`${principle.key}-heading`">
      <h2 :id="`${principle.key}-heading`" class="font-bold">{{ t(`${principle.key}.title`) }}</h2>
      <p>{{ t(`${principle.key}.subtitle`) }}</p>

      <template v-for="(criterion, index) in principle.criteria" :key="criterion.key">
        <hr v-if="index > 0" class="my-9 w-[119px] border-tw-ink/40" />
        <h3 class="mt-7 font-normal">{{ t(`${principle.key}.${criterion.key}.title`) }}</h3>
        <ul class="list-disc pl-[60px]">
          <li v-for="item in criterion.items" :key="item.key">
            {{ t(`${principle.key}.${criterion.key}.${item.key}`) }}
            <ul v-if="item.children" class="list-[circle] pl-8">
              <li v-for="child in item.children" :key="child">{{ t(`${principle.key}.${criterion.key}.${child}`) }}</li>
            </ul>
          </li>
        </ul>
      </template>
    </section>

    <PillLink class="mt-16" :href="statementUrl" external>{{ t('openStatement') }}</PillLink>
  </InfoPageLayout>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'

  import InfoPageLayout from '@/components/landingpage/InfoPageLayout.vue'
  import PillLink from '@/components/landingpage/PillLink.vue'

  // Published statement in Digdir's uustatus register
  const statementUrl = 'https://uustatus.no/nb/erklaringer/publisert/f87a233a-a0b3-46fa-a3dc-59e8026f5544'

  interface Item {
    key: string
    children?: string[]
  }

  interface Criterion {
    key: string
    items: Item[]
  }

  interface Principle {
    key: string
    criteria: Criterion[]
  }

  const principles: Principle[] = [
    {
      key: 'perceivable',
      criteria: [
        { key: 'c131', items: [{ key: 'i1' }, { key: 'i2', children: ['i3', 'i4', 'i5', 'i6'] }] },
        { key: 'c143', items: [{ key: 'i1' }, { key: 'i2' }] },
        { key: 'c1411', items: [{ key: 'i1' }, { key: 'i2' }] },
      ],
    },
    {
      key: 'operable',
      criteria: [
        { key: 'c211', items: [{ key: 'i1' }, { key: 'i2', children: ['i3', 'i4'] }] },
        { key: 'c247', items: [{ key: 'i1' }, { key: 'i2', children: ['i3'] }] },
      ],
    },
    {
      key: 'understandable',
      criteria: [{ key: 'c311', items: [{ key: 'i1' }] }],
    },
  ]

  const { t } = useI18n({
    messages: {
      no: {
        title: 'Tilgjengelighetserklæring for Samtavla',
        intro: 'Det ble gjort en UU undersøkelse av Samtavla, og oppsummert er rapporten:',
        openStatement: 'Åpne erklæringen',
        perceivable: {
          title: 'Prinsipp 1 – Mulig å oppfatte',
          subtitle: '(Innhold må presenteres på måter brukere kan oppfatte)',
          c131: {
            title: '1.3.1 Informasjon og relasjoner (Nivå A)',
            i1: 'Struktur og relasjoner i innholdet mangler eller er ikke korrekt kodet.',
            i2: 'Eksempler på feil:',
            i3: 'Feil bruk av overskrifter',
            i4: 'Manglende semantisk HTML',
            i5: 'Listeelementer som ikke er kodet som liste',
            i6: 'Visuell struktur uten tilsvarende programmatisk struktur',
          },
          c143: {
            title: '1.4.3 Kontrast (minimum) – Nivå AA',
            i1: 'For lav kontrast mellom tekst og bakgrunn',
            i2: 'Brukere med nedsatt syn har problemer med å lese tekst',
          },
          c1411: {
            title: '1.4.11 Kontrast for ikke-tekstlig innhold – Nivå AA',
            i1: 'Ikoner, knapper, rammer og grafiske elementer har for dårlig kontrast',
            i2: 'Gjelder altså grafikk uten tekst, men som brukes for å forstå innhold eller funksjon',
          },
        },
        operable: {
          title: 'Prinsipp 2 – Mulig å betjene',
          subtitle: '(Brukere skal kunne navigere og bruke alt innhold)',
          c211: {
            title: '2.1.1 Tastatur – Nivå A',
            i1: 'Ikke alle funksjoner er mulig å bruke kun med tastatur',
            i2: 'Mulige problemer:',
            i3: 'Elementer som ikke kan fokuseres',
            i4: '«Tastaturfeller» hvor man ikke kommer videre',
          },
          c247: {
            title: '2.4.7 Synlig fokus – Nivå AA',
            i1: 'Når brukeren navigerer med tastatur, er det ikke tydelig hvilket element som har fokus',
            i2: 'Eksempel:',
            i3: 'Fokusrammen rundt lenker/knapper er utydelig eller fjernet',
          },
        },
        understandable: {
          title: 'Prinsipp 3 – Forståelig',
          subtitle: '(Brukere skal forstå innholdet og hvordan nettstedet fungerer)',
          c311: {
            title: '3.1.1 Språk på siden – Nivå A',
            i1: 'Nettstedet mangler korrekt angivelse av hovedspråket i HTML-koden',
          },
        },
      },
      en: {
        title: 'Accessibility statement for Talkwall',
        intro: 'A universal design (accessibility) review of Talkwall was carried out. In summary, the report found:',
        openStatement: 'Open the statement',
        perceivable: {
          title: 'Principle 1 – Perceivable',
          subtitle: '(Content must be presented in ways users can perceive)',
          c131: {
            title: '1.3.1 Info and relationships (Level A)',
            i1: 'Structure and relationships in the content are missing or not coded correctly.',
            i2: 'Examples of errors:',
            i3: 'Incorrect use of headings',
            i4: 'Missing semantic HTML',
            i5: 'List items not coded as lists',
            i6: 'Visual structure without matching programmatic structure',
          },
          c143: {
            title: '1.4.3 Contrast (minimum) – Level AA',
            i1: 'Contrast between text and background is too low',
            i2: 'Users with low vision have difficulty reading text',
          },
          c1411: {
            title: '1.4.11 Non-text contrast – Level AA',
            i1: 'Icons, buttons, borders and graphical elements have insufficient contrast',
            i2: 'This applies to graphics without text that are needed to understand content or functionality',
          },
        },
        operable: {
          title: 'Principle 2 – Operable',
          subtitle: '(Users must be able to navigate and use all content)',
          c211: {
            title: '2.1.1 Keyboard – Level A',
            i1: 'Not all functions can be used with a keyboard alone',
            i2: 'Possible problems:',
            i3: 'Elements that cannot receive focus',
            i4: '“Keyboard traps” where you cannot move on',
          },
          c247: {
            title: '2.4.7 Focus visible – Level AA',
            i1: 'When navigating with a keyboard, it is not clear which element has focus',
            i2: 'Example:',
            i3: 'The focus outline around links/buttons is unclear or removed',
          },
        },
        understandable: {
          title: 'Principle 3 – Understandable',
          subtitle: '(Users must understand the content and how the website works)',
          c311: {
            title: '3.1.1 Language of page – Level A',
            i1: 'The website does not correctly declare its main language in the HTML',
          },
        },
      },
    },
  })
</script>
